/**
 * The incoming ring, played from a file rather than built from oscillators.
 *
 * This exists for the desktop app and only for it. An `AudioContext` starts
 * suspended and only a user gesture wakes it, which is a fair trade in a
 * browser tab that nobody reaches without clicking something first. It is the
 * wrong trade for an app whose entire job is to sit in the Dock and wait: open
 * it in the morning, touch nothing, and the first call of the day arrives in
 * silence. A media element has no such gate inside the app, because wry turns
 * media autoplay on (`setMediaTypesRequiringUserActionForPlayback(None)`), so
 * the same ring plays with nothing having been clicked.
 *
 * The file is one second of tone. The four seconds of silence that follow are
 * the gap between plays rather than part of the asset, which keeps it at 44 KB
 * and lets the volume slider take effect between bursts, exactly as it does for
 * the oscillator ring.
 */
import { readAudioVolume } from "../audio/volume";
import { CYCLE_SEC, fileRingVolume, type Ring } from "./ringSpec";

/** `setSinkId` is not in every lib.dom this repo compiles against. */
type SinkCapableElement = HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };

/**
 * Start ringing from `url`, on `deviceId` when one is chosen.
 *
 * Returns null when the element refuses to play, so the caller can fall back to
 * the oscillator ring rather than leaving the operator with nothing. A refusal
 * here is not a bug to swallow: it is the browser autoplay policy, and the
 * oscillator path knows how to wait for a gesture.
 */
export async function startFileRing(url: string, deviceId: string): Promise<Ring | null> {
  if (typeof document === "undefined") return null;

  const el = document.createElement("audio") as SinkCapableElement;
  el.src = url;
  el.preload = "auto";
  el.setAttribute("aria-hidden", "true");
  el.style.display = "none";
  document.body.appendChild(el);

  const drop = () => {
    el.pause();
    el.remove();
  };

  // Read per burst, not once at the top, for the same reason the oscillator
  // ring does: a ring lasts as long as somebody takes to reach the phone, which
  // is exactly long enough to turn it down.
  const applyVolume = () => {
    el.volume = fileRingVolume(readAudioVolume().ring);
  };

  if (deviceId && typeof el.setSinkId === "function") {
    // The wrong speaker beats no speaker, so a refused sink is not fatal.
    await el.setSinkId(deviceId).catch(() => {});
  }

  applyVolume();
  try {
    await el.play();
  } catch {
    drop();
    return null;
  }

  const timer = window.setInterval(() => {
    applyVolume();
    if (el.volume <= 0) return;
    el.currentTime = 0;
    void el.play().catch(() => {});
  }, CYCLE_SEC * 1000);

  return {
    stop: () => {
      window.clearInterval(timer);
      drop();
    },
  };
}
